import { useMemo, useCallback } from 'react'
import moment from 'moment'

import { buildSuggestedDatesParamsKey, getShouldUseSearchPrices } from 'lib/search/searchUtils'
import { getCanViewLuxPlusBenefits, getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { getSuggestedDates } from 'selectors/offerSelectors'
import { getSumExtraGuestsSurcharge } from 'lib/checkout/cartReservationUtils'
import { logNewRelic } from '../../services/newRelic'
import { useAppSelector } from 'hooks/reduxHooks'
import useOfferMetaData from '../Offers/useOfferMetaData'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import { determineMemberPricing, onlyNonZeroFees } from 'lib/offer/pricing'
import { isBundlePackageWithPrice, isBundleOffer } from 'lib/offer/offerTypes'
import useFlightAndHotelBundle from '../FlightAndHotelBundle/useFlightAndHotelBundle'
import { getBundleValueUp } from 'lib/bundleAndSave/getBundleValueUp'
import { getIsSpecificCampaignPage } from 'selectors/routerSelectors'
import { CHARTER_PAGE_SLUG } from 'constants/charterFlight'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { useMaldivesDirectBundleContext } from 'components/MaldivesDirect/MaldivesDirectSearchPage/MaldivesDirectBundleContext'

interface Props {
  offer: App.Offer | App.OfferSummary
  pkg?: App.Package | App.BundlePackageWithPrice
  rate?: App.OfferAvailableRate
  duration?: number
  filters?: App.OfferListFilters
  useRealSearchPrices?: boolean // use prices directly from search api
  showPerNightPricing?: boolean
}

interface UseSearchPricesResult {
  /** @deprecated - use defaultPricing` instead */
  totalPrice: number
  /** @deprecated - use memberPricing instead */
  totalMemberPrice?: number
  /** @deprecated - use defaultPricing instead */
  totalValueBase: number
  /** @deprecated - use defaultPricing` instead */
  discountPercent: number
  hotelPrice: number
  hotelMemberPrice: number
  showMemberPrice: boolean
  propertyFees: number
  suggestedDates?: App.OfferSuggestedDates
  defaultPricing: App.PricingWithValue
  memberPricing?: App.PricingWithValue
  /** Bundle pricing (hotel + flight) when bundle mode is enabled - total price */
  bundlePricing?: App.PricingWithValue & { bundleAddonPrice: number }
  bundleMemberPricing?: App.PricingWithValue & { bundleAddonPrice: number }
  /** Bundle pricing per person */
  bundlePricingPerPerson?: App.PricingWithValue & { bundleAddonPrice: number }
  bundleMemberPricingPerPerson?: App.PricingWithValue & { bundleAddonPrice: number }
  /** Bundle value (valuedUpTo) for bundle offers */
  bundleValue?: number
  /** Bundle value per person */
  bundleValuePerPerson?: number
}

export function useDirectSearchPrices({ filters, offerId }: { filters: App.OfferListFilters, offerId: string }): App.OfferListMetadataPricing | undefined {
  const offerMetadata = useOfferMetaData(offerId, filters)
  if (getShouldUseSearchPrices(filters)) return offerMetadata?.pricing
}

function calculatePerNightPrice(price: number, duration: number = 1) {
  return Math.round(price / duration)
}

export function useSearchPrices({ offer, pkg, rate, filters, useRealSearchPrices = false, showPerNightPricing = false }: Props): UseSearchPricesResult {
  const luxPlusEnabled = useAppSelector(getIsLuxPlusEnabled)
  const offerMetadata = useOfferMetaData(offer.id, filters)
  const canViewLuxPlusBenefits = useAppSelector(getCanViewLuxPlusBenefits)
  const flexibleSearchFilterKey = buildSuggestedDatesParamsKey(filters?.flexibleMonths, filters?.flexibleNights, filters?.rooms)
  const suggestedDates = useAppSelector((state) => getSuggestedDates(state, flexibleSearchFilterKey, offer.id))
  const isFlexibleSearch = filters?.flexibleMonths || filters?.flexibleNights

  // Flight and Hotel bundle data
  const flightAndHotelBundle = useFlightAndHotelBundle()
  const isMaldivesCharter = useAppSelector((state: App.State) => getIsSpecificCampaignPage(state, CHARTER_PAGE_SLUG))
  const { charterBundle } = useMaldivesDirectBundleContext()

  // Verify dates match for bundle calculation
  const datesMatch = flightAndHotelBundle.selectedDates.checkIn === filters?.checkIn && flightAndHotelBundle.selectedDates.checkOut === filters?.checkOut

  // Calculate bundle addon price
  const bundleAddonPrice = useMemo(() => {
    const addonPrice = (isMaldivesCharter && datesMatch && flightAndHotelBundle.flightData.totalPrice)
      ? flightAndHotelBundle.flightData.totalPrice
      : 0

    return addonPrice
  }, [isMaldivesCharter, datesMatch, flightAndHotelBundle.flightData.totalPrice])

  // Helper function to add flight price to existing pricing
  const addFlightPriceToExistingPricing = useCallback((pricing: App.PricingWithValue, addonPrice: number, bundleValue?: number): App.PricingWithValue & { bundleAddonPrice: number } => {
    const newPrice = pricing.price + addonPrice
    // Use bundleValue if provided (hotel value + flight value), otherwise use pricing.value (hotel value only)
    // This ensures discount calculation is correct when flight has value
    const totalValue = bundleValue ?? pricing.value
    return {
      ...pricing,
      price: newPrice,
      value: totalValue,
      discountPercent: getValueOffPercent(totalValue, newPrice),
      bundleAddonPrice: addonPrice,
    }
  }, [])
  const rateOrPkg = (rate ?? pkg)
  const baseMemberPrice = rateOrPkg?.memberPrice || 0
  const showMemberPrice = luxPlusEnabled && baseMemberPrice > 0
  const rateOrPkgMemberPrice = rate?.memberPriceWithSurcharge ?? pkg?.memberPrice
  const rooms = useMemo(() => filters?.rooms || [], [filters?.rooms])

  // Calculate total passengers for per-person pricing
  const totalPassengers = useMemo(
    () => sumUpOccupancies(rooms),
    [rooms],
  )

  // Helper function to convert pricing to per-person (always converts)
  const applyPerPersonPricing = useCallback((pricing: App.PricingWithValue & { bundleAddonPrice: number }): App.PricingWithValue & { bundleAddonPrice: number } => {
    if (totalPassengers === 0) {
      return pricing
    }

    const perPersonPrice = pricing.price / totalPassengers
    const perPersonValue = pricing.value / totalPassengers

    return {
      ...pricing,
      price: perPersonPrice,
      value: perPersonValue,
      // Recalculate discount percent for per-person pricing
      discountPercent: getValueOffPercent(perPersonValue, perPersonPrice),
    }
  }, [totalPassengers])
  const priceMultiplier = rate ? 1 : (rooms.length || 1)
  const propertyFees = (rate?.propertyFees ?? pkg?.propertyFees ?? 0) * priceMultiplier
  const extraGuestSurcharges = useMemo(() => {
    if (!pkg) return 0

    if (isBundlePackageWithPrice(pkg)) {
      return 0
    }
    return getSumExtraGuestsSurcharge(rooms, offer, pkg)
  }, [pkg, rooms, offer])
  const hotelPrice = ((rateOrPkg?.price ?? 0) + (pkg?.surcharge ?? 0)) * priceMultiplier + extraGuestSurcharges
  const hotelMemberPrice = showMemberPrice ? ((rateOrPkgMemberPrice ?? 0 + (pkg?.surcharge ?? 0)) * priceMultiplier + extraGuestSurcharges) : 0
  const totalPrice = isFlexibleSearch && suggestedDates?.price ? suggestedDates?.price : hotelPrice + propertyFees
  const pkgValueOrMemberValue = canViewLuxPlusBenefits ? pkg?.memberValue : pkg?.value
  const totalValueBase = rate?.hotelValue ?? suggestedDates?.value ?? (pkgValueOrMemberValue ?? 0 * (rooms.length || 1) + extraGuestSurcharges)
  const discountPercent = pkg?.discountPercent || getValueOffPercent(totalValueBase, showMemberPrice && canViewLuxPlusBenefits ? hotelMemberPrice : hotelPrice)
  const surcharge = (rateOrPkg?.surcharge ?? 0) * priceMultiplier
  const taxesAndFees = (rateOrPkg?.taxesAndFees ?? 0) * priceMultiplier

  const totalMemberPrice = useMemo(() => {
    if (showMemberPrice) {
      let memberPrice: number
      if (isFlexibleSearch && suggestedDates?.memberPrice) {
        memberPrice = suggestedDates.memberPrice
      } else {
        memberPrice = hotelMemberPrice + propertyFees
      }
      return memberPrice
    }
  }, [showMemberPrice, isFlexibleSearch, suggestedDates, hotelMemberPrice, propertyFees])

  // Calculate bundle value for bundle offers
  // For Maldives Charter: also calculate bundle value for regular hotel offers (hotel value + flight value)
  const bundleValue = useMemo(() => {
    // For bundle offers, use getBundleValueUp
    if (isBundleOffer(offer) && pkg && isBundlePackageWithPrice(pkg)) {
      const bundleValueResult = getBundleValueUp(offer, pkg, canViewLuxPlusBenefits)
      return bundleValueResult.valuedUpTo
    }

    // For Maldives Charter with regular hotel offers: hotel value + flight value
    if (isMaldivesCharter && bundleAddonPrice > 0 && pkg) {
      const pkgValueOrMemberValue = canViewLuxPlusBenefits ? pkg.memberValue : pkg.value
      return (pkgValueOrMemberValue ?? 0) + bundleAddonPrice
    }

    return undefined
  }, [offer, pkg, canViewLuxPlusBenefits, isMaldivesCharter, bundleAddonPrice])

  // Calculate bundle value per person
  const bundleValuePerPerson = useMemo(() => {
    if (bundleValue !== undefined && totalPassengers > 0) {
      return bundleValue / totalPassengers
    }
    return undefined
  }, [bundleValue, totalPassengers])

  return useMemo<UseSearchPricesResult>(() => {
    const fees = onlyNonZeroFees([
      { type: 'property', amount: propertyFees },
      { type: 'taxesAndFees', amount: taxesAndFees },
      { type: 'surcharge', amount: surcharge },
      { type: 'extraGuestSurcharge', amount: extraGuestSurcharges },
    ])

    if (useRealSearchPrices) {
      if (!offerMetadata?.pricing) {
        logNewRelic('No pricing found for offer from search', { offerId: offer.id, filters }, 'error')
        return {
          totalPrice: 0,
          totalMemberPrice: 0,
          totalValueBase: 0,
          discountPercent: 0,
          hotelPrice: 0,
          hotelMemberPrice: 0,
          showMemberPrice: false,
          propertyFees: 0,
          suggestedDates,
          defaultPricing: {
            price: 0,
            value: 0,
            discountPercent: 0,
            saleUnit: offer.saleUnit,
            fees: [],
          },
          memberPricing: undefined,
          bundlePricing: undefined,
          bundleMemberPricing: undefined,
          bundlePricingPerPerson: undefined,
          bundleMemberPricingPerPerson: undefined,
          bundleValue: undefined,
          bundleValuePerPerson: undefined,
        }
      }

      const fallbackPrice = offer.lowestPricePackage
      const pricing = offerMetadata?.pricing

      // For charter bundle, use actual package data instead of metadata pricing
      // Metadata pricing is inaccurate because backend doesn't receive checkIn/checkOut params
      let actualPackage = pkg
      if (charterBundle && filters?.checkIn && filters?.checkOut && offer.packages) {
        const requestedDuration = moment(filters.checkOut).diff(filters.checkIn, 'days')
        const matchingPackage = offer.packages.find(p => p.duration === requestedDuration)
        if (matchingPackage) {
          actualPackage = matchingPackage
        }
      }

      const discountPercent = (() => {
        // For charter bundle, use actual package values
        if (charterBundle && actualPackage) {
          return getValueOffPercent(actualPackage.value ?? 0, actualPackage.price ?? 0)
        }
        if (showMemberPrice && canViewLuxPlusBenefits && pricing.lowestMemberPrice !== undefined && pricing.lowestPriceValue !== undefined) {
          return getValueOffPercent(pricing.lowestPriceValue, pricing.lowestMemberPrice)
        }
        if (pricing.lowestPrice && pricing.lowestPriceValue !== undefined) {
          return getValueOffPercent(pricing.lowestPriceValue, pricing.lowestPrice)
        }
        return getValueOffPercent(fallbackPrice?.value ?? 0, fallbackPrice?.price ?? 0)
      })()

      // For charter bundle, use actual package prices instead of metadata
      // package.price is per room, so we need to multiply by number of rooms
      const charterBundlePriceMultiplier = rate ? 1 : (rooms.length || 1)
      const totalPrice = charterBundle && actualPackage
        ? (actualPackage.price ?? 0) * charterBundlePriceMultiplier
        : (pricing.lowestPrice ?? fallbackPrice?.price ?? 0)
      const totalMemberPrice = charterBundle && actualPackage
        ? (actualPackage.memberPrice ?? 0) * charterBundlePriceMultiplier
        : (pricing?.lowestMemberPrice ?? fallbackPrice?.memberPrice ?? 0)
      const totalValueBase = charterBundle && actualPackage
        ? (actualPackage.value ?? 0) * charterBundlePriceMultiplier
        : (pricing?.lowestPriceValue ?? fallbackPrice?.value ?? 0)

      // Calculate bundle value for bundle offers
      // For Maldives Charter: also calculate bundle value for regular hotel offers (hotel value + flight value)
      const bundleValue = (() => {
        // For bundle offers, use getBundleValueUp
        if (isBundleOffer(offer) && actualPackage && isBundlePackageWithPrice(actualPackage)) {
          const bundleValueResult = getBundleValueUp(offer, actualPackage, canViewLuxPlusBenefits)
          return bundleValueResult.valuedUpTo
        }

        // For Maldives Charter with regular hotel offers: hotel value + flight value
        // package.value is per room, so we need to multiply by number of rooms
        if (isMaldivesCharter && bundleAddonPrice > 0 && actualPackage) {
          const pkgValueOrMemberValue = canViewLuxPlusBenefits ? actualPackage.memberValue : actualPackage.value
          return (pkgValueOrMemberValue ?? 0) * charterBundlePriceMultiplier + bundleAddonPrice
        }

        return undefined
      })()

      // Calculate bundle value per person
      const bundleValuePerPerson = (() => {
        if (bundleValue !== undefined && totalPassengers > 0) {
          return bundleValue / totalPassengers
        }
        return undefined
      })()

      const defaultPricing: App.PricingWithValue = {
        price: totalPrice,
        value: totalValueBase,
        discountPercent: getValueOffPercent(totalValueBase, totalPrice),
        saleUnit: offer.saleUnit,
        fees,
      }

      return {
        totalPrice: showPerNightPricing ? calculatePerNightPrice(totalPrice, pkg?.duration) : totalPrice,
        totalMemberPrice,
        totalValueBase,
        discountPercent,
        hotelPrice: charterBundle && actualPackage ? (actualPackage.price ?? 0) * charterBundlePriceMultiplier : (pricing.lowestPrice ?? fallbackPrice?.price ?? 0),
        hotelMemberPrice: charterBundle && actualPackage ? (actualPackage.memberPrice ?? 0) * charterBundlePriceMultiplier : (pricing.lowestMemberPrice ?? fallbackPrice?.memberPrice ?? 0),
        showMemberPrice,
        propertyFees: charterBundle && actualPackage ? (actualPackage.propertyFees ?? 0) * charterBundlePriceMultiplier : (fallbackPrice?.propertyFees ?? 0),
        suggestedDates,
        defaultPricing,
        memberPricing: determineMemberPricing(defaultPricing, totalMemberPrice, totalValueBase),
        // Bundle pricing: add flight price to existing hotel pricing (total price)
        // Pass bundleValue to ensure discount calculation is correct (hotel value + flight value)
        bundlePricing: bundleAddonPrice > 0 ? addFlightPriceToExistingPricing(defaultPricing, bundleAddonPrice, bundleValue) : undefined,
        bundleMemberPricing: bundleAddonPrice > 0 && determineMemberPricing(defaultPricing, totalMemberPrice, totalValueBase)
          ? addFlightPriceToExistingPricing(determineMemberPricing(defaultPricing, totalMemberPrice, totalValueBase)!, bundleAddonPrice, bundleValue)
          : undefined,
        // Bundle pricing per person
        bundlePricingPerPerson: bundleAddonPrice > 0 ? applyPerPersonPricing(addFlightPriceToExistingPricing(defaultPricing, bundleAddonPrice, bundleValue)) : undefined,
        bundleMemberPricingPerPerson: bundleAddonPrice > 0 && determineMemberPricing(defaultPricing, totalMemberPrice, totalValueBase)
          ? applyPerPersonPricing(addFlightPriceToExistingPricing(determineMemberPricing(defaultPricing, totalMemberPrice, totalValueBase)!, bundleAddonPrice, bundleValue))
          : undefined,
        bundleValue,
        bundleValuePerPerson,
      }
    }

    const defaultPricing: App.PricingWithValue = {
      price: showPerNightPricing ? calculatePerNightPrice(totalPrice, pkg?.duration) : totalPrice,
      value: totalValueBase,
      discountPercent: getValueOffPercent(totalValueBase, totalPrice),
      saleUnit: offer.saleUnit,
      fees,
    }

    return {
      totalPrice: showPerNightPricing ? calculatePerNightPrice(totalPrice, pkg?.duration) : totalPrice,
      totalMemberPrice,
      totalValueBase,
      discountPercent,
      hotelPrice,
      hotelMemberPrice,
      showMemberPrice,
      propertyFees,
      suggestedDates,
      defaultPricing,
      memberPricing: determineMemberPricing(defaultPricing, totalMemberPrice, totalValueBase),
      // Bundle pricing: add flight price to existing hotel pricing (total price)
      // Pass bundleValue to ensure discount calculation is correct (hotel value + flight value)
      bundlePricing: bundleAddonPrice > 0 ? addFlightPriceToExistingPricing(defaultPricing, bundleAddonPrice, bundleValue) : undefined,
      bundleMemberPricing: bundleAddonPrice > 0 && determineMemberPricing(defaultPricing, totalMemberPrice, totalValueBase)
        ? addFlightPriceToExistingPricing(determineMemberPricing(defaultPricing, totalMemberPrice, totalValueBase)!, bundleAddonPrice, bundleValue)
        : undefined,
      // Bundle pricing per person
      bundlePricingPerPerson: bundleAddonPrice > 0 ? applyPerPersonPricing(addFlightPriceToExistingPricing(defaultPricing, bundleAddonPrice, bundleValue)) : undefined,
      bundleMemberPricingPerPerson: bundleAddonPrice > 0 && determineMemberPricing(defaultPricing, totalMemberPrice, totalValueBase)
        ? applyPerPersonPricing(addFlightPriceToExistingPricing(determineMemberPricing(defaultPricing, totalMemberPrice, totalValueBase)!, bundleAddonPrice, bundleValue))
        : undefined,
      bundleValue,
      bundleValuePerPerson,
    }
  }, [propertyFees, taxesAndFees, surcharge, extraGuestSurcharges, useRealSearchPrices, showPerNightPricing, totalPrice, pkg, totalValueBase, offer, totalMemberPrice, discountPercent, hotelPrice, hotelMemberPrice, showMemberPrice, suggestedDates, bundleAddonPrice, addFlightPriceToExistingPricing, bundleValue, applyPerPersonPricing, bundleValuePerPerson, offerMetadata?.pricing, charterBundle, filters, rate, rooms.length, canViewLuxPlusBenefits, isMaldivesCharter, totalPassengers])
}
